/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 */

/**
 * Styles for Quick Schedule add-to-assignment slot picker modal.
 *
 * @file quickScheduleAddToAssignmentModalStyles.js
 * @author Anuja Gaikwad
 * @since 2.4.3
 */

import { styled } from '@mui/material/styles';
import Paper from '@mui/material/Paper';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import mixins from '../../../assets/styles/mixins.scss';
import CommonStyles from '../../../assets/styles/CommonStyles';

const contentScrollStyles = CommonStyles().scrollContainer({
  scrollbarWidth: 8,
  scrollbarColor: `${mixins.quaternary} transparent`,
  overflowY: 'auto'
});

export const QuickScheduleAddToAssignmentDialogPaper = styled(Paper)({
  '&&': {
    borderRadius: 24,
    padding: 48,
    width: 640,
    maxWidth: 'calc(100vw - 32px)',
    boxSizing: 'border-box',
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(13, 0, 77, 0.04), 0 1px 0.5px rgba(13, 0, 77, 0.1), 0 2px 0.5px rgba(13, 0, 77, 0.15)',
    position: 'relative'
  }
});

export const QuickScheduleAddToAssignmentCloseButton = styled(IconButton)({
  '&&': {
    position: 'absolute',
    top: 16,
    right: 16,
    padding: 4,
    color: mixins.color_text_primary
  }
});

export const QuickScheduleAddToAssignmentDialogContent = styled(DialogContent)({
  '&&': {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    padding: 0,
    minHeight: 0,
    flex: 1,
    width: '100%',
    minWidth: 0,
    overflow: 'hidden'
  }
});

export const QuickScheduleAddToAssignmentTitle = styled(Typography)({
  '&&': {
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: 24,
    fontWeight: mixins.fontWeight500,
    lineHeight: '32px',
    letterSpacing: '-0.48px',
    color: mixins.color_text_primary,
    margin: 0
  }
});

export const QuickScheduleAddToAssignmentSubtitle = styled(Typography)({
  '&&': {
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize14,
    fontWeight: mixins.fontWeight500,
    lineHeight: '16px',
    color: mixins.greyScaleColor
  }
});

export const QuickScheduleAddToAssignmentScrollPane = styled('div')({
  '&&': {
    backgroundColor: '#f6f6fb',
    borderRadius: 16,
    padding: 16,
    boxSizing: 'border-box',
    maxHeight: 532,
    minHeight: 200,
    ...contentScrollStyles
  }
});

export const QuickScheduleAddToAssignmentListWrapper = styled('div')({
  '&&': {
    display: 'flex',
    flexDirection: 'column',
    gap: 8
  }
});

export const QuickScheduleAddToAssignmentFooter = styled('div')({
  '&&': {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 8,
    width: '100%',
    paddingTop: 8
  }
});

export const QuickScheduleAddToNewAssignmentButton = styled('button')({
  '&&': {
    appearance: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
    height: 40,
    minHeight: 40,
    padding: '10px 20px',
    borderRadius: 1000,
    border: `2px solid ${mixins.navyColor}`,
    backgroundColor: 'transparent',
    color: mixins.navyColor,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    fontWeight: mixins.fontWeight500,
    lineHeight: '20px',
    whiteSpace: 'nowrap',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: mixins.lightThemeBgColor2
    },
    '&:focus-visible': {
      outline: `2px solid ${mixins.navyColor}`,
      outlineOffset: 2
    },
    '&:disabled': {
      opacity: 0.5,
      cursor: 'not-allowed'
    }
  }
});

export const QuickScheduleAddToAssignmentDoneButton = styled('button')({
  '&&': {
    appearance: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
    height: 40,
    minHeight: 40,
    padding: '10px 20px',
    borderRadius: 1000,
    border: 'none',
    backgroundColor: mixins.amethyst,
    color: mixins.chalkWhiteFont,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    fontWeight: mixins.fontWeight500,
    lineHeight: '16px',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: mixins.amethystColorV2
    },
    '&:focus-visible': {
      outline: `2px solid ${mixins.navyColor}`,
      outlineOffset: 2
    },
    '&:disabled': {
      opacity: 0.5,
      cursor: 'not-allowed'
    }
  }
});
